/**
 * mcpbPresence.js — pure helpers for the MCPlayback calendar's recording-availability dots.
 *
 * A direct port of the static logic in dview `MCPresenceModel.java` (Java parity, #86bb7phhh): the
 * lite calendar shows the SAME per-day green "footage" dots the Java `MCCalendarPopup` does, fed by
 * the SAME `event.getEventPresence` RPC at DAY granularity. Nothing on the backend changes — that RPC
 * already ships (rda-backend `modules/event.getEventPresence` -> `db/event.getPresenceByTime`).
 *
 * The RPC returns, per camera, the LOCAL day buckets that contain recorded video, mapped to the total
 * recorded seconds in that bucket:
 *     { '<bCamera>': { 'YYYYMMDD': <seconds>, ... } }        (granularity 'day')
 * Dots are BINARY and merged across the camera set — ANY camera recording on a day lights that day —
 * exactly like Java `dayDots()`. The seconds magnitude is only used by the phase-2 hour strip.
 *
 * Timestamps stay tz-naive local wall-clock ('YYYYMMDDHHMMSS'), same convention as mcpbTime.js: the
 * backend buckets by the stored local prefix, so no epoch/tz conversion happens here.
 */

const pad2 = (n) => ('0' + n).slice(-2);

/** A day-dot array mirroring Java's `boolean[32]`: index by day-of-month 1..31 (index 0 unused). */
export const emptyDayDots = () => new Array(32).fill(false);

/**
 * Stable signature of a camera set: sorted distinct "serial:camera" pairs (placeholder/unassigned
 * slots skipped), joined by ",". Two sets that would query the same presence share a signature, so the
 * cache key is set-scoped and a set change is detectable without deep-comparing. Mirrors Java
 * `cameraSetSignature`. `slots` = the mcplayback slot array ({ bSerial, bCamera } | null).
 */
export const cameraSetSignature = (slots) => {
  if (!Array.isArray(slots)) return '';
  const parts = new Set();
  slots.forEach((c) => {
    if (!c || !c.bCamera) return; // null placeholder / camera 0 == unassigned
    parts.add(`${c.bSerial}:${c.bCamera}`);
  });
  return Array.from(parts).sort().join(',');
};

/** "YYYYMM" month key fragment (month0 is 0-based). Mirrors Java `monthKey`. */
export const monthKey = (year, month0) => `${year}${pad2(month0 + 1)}`;

/**
 * The 14-digit local [start, end] range covering an entire month at day granularity — end is the last
 * second of the last day. Mirrors Java `monthRange`. `new Date(year, month0+1, 0)` yields the month's
 * last day (day 0 of the next month); days-in-month is tz-independent so this is safe.
 */
export const monthRange = (year, month0) => {
  const last = new Date(year, month0 + 1, 0).getDate();
  const mm = pad2(month0 + 1);
  return [`${year}${mm}01000000`, `${year}${mm}${pad2(last)}235959`];
};

/**
 * Parse the `event.getEventPresence` result into a per-camera bucketKey->seconds map. Tolerant of the
 * shapes the JSON-RPC layer can produce: an outer object whose values are inner objects of
 * bucketString -> numeric seconds (a non-numeric value counts as 0; anything unexpected -> {}). Mirrors
 * Java `parsePresence`.
 */
export const parsePresence = (result) => {
  const out = {};
  if (!result || typeof result !== 'object' || Array.isArray(result)) return out;
  Object.keys(result).forEach((cam) => {
    const v = result[cam];
    const buckets = {};
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.keys(v).forEach((b) => {
        const n = Number(v[b]);
        buckets[b] = Number.isFinite(n) ? n : 0;
      });
    }
    out[String(cam)] = buckets;
  });
  return out;
};

/**
 * Merge per-camera day buckets into recording-day dots for one month: `out[d]` is true when ANY camera
 * recorded any seconds on day `d` of (year, month0). Binary — the magnitude is ignored (phase-2 hour
 * strip only). Index 0 unused; days 1..31. Mirrors Java `dayDots`.
 */
export const dayDots = (presence, year, month0) => {
  const dots = emptyDayDots();
  if (!presence || typeof presence !== 'object') return dots;
  const prefix = monthKey(year, month0); // YYYYMM
  Object.keys(presence).forEach((cam) => {
    const buckets = presence[cam];
    if (!buckets || typeof buckets !== 'object') return;
    Object.keys(buckets).forEach((b) => {
      if (!b || b.length < 8) return;
      if (b.slice(0, 6) !== prefix) return;
      if (!(Number(buckets[b]) > 0)) return;
      const d = parseInt(b.slice(6, 8), 10);
      if (d >= 1 && d <= 31) dots[d] = true;
    });
  });
  return dots;
};

/** Convenience: parse a raw RPC payload straight to a month's day dots. */
export const dayDotsFromResult = (result, year, month0) => dayDots(parsePresence(result), year, month0);

/** OR two day-dot arrays (merge presence across NVRs for an eview camera set). */
export const orDayDots = (a, b) => {
  const out = emptyDayDots();
  for (let d = 1; d <= 31; d += 1) out[d] = !!(a && a[d]) || !!(b && b[d]);
  return out;
};

/** True if the month has any footage day (used to size/label the calendar's empty state). */
export const hasAnyDot = (dots) => Array.isArray(dots) && dots.some(Boolean);

/**
 * True only for the fault an older backend raises when it lacks `event.getEventPresence` — the signal to
 * latch "unsupported" and stop re-querying (calendar stays dot-free, still fully usable as a plain
 * date+time picker). Mirrors Java `isUnknownMethodFault`: over JSON-RPC the fork returns
 * `Fault(-32601, 'Method X not supported.')` -> `res.data.error = { code: -32601, message: ... }`. A
 * network/timeout/other error does NOT match, so it reads as transient (retry next open), not permanent.
 * Accepts a JSON-RPC error object ({ code, message }), an Error, or a string.
 */
export const isUnsupportedPresenceError = (err) => {
  if (!err) return false;
  if (err.code === -32601) return true; // JSON-RPC "method not found"
  const msg = (
    (typeof err === 'string' && err)
    || err.message
    || err.faultString
    || ''
  ).toLowerCase();
  return msg.indexOf('not supported') >= 0
    || msg.indexOf('method not found') >= 0
    || msg.indexOf('unknown method') >= 0;
};
